/**
 * Admin entity definitions + list/inline field metadata.
 */

export const ENTITIES = {
  rooms: {
    label: 'Rooms',
    path: 'rooms',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'cabin_no', label: 'Cabin No' },
      { key: 'cabin_type', label: 'Type' },
      { key: 'location', label: 'Location' },
      { key: 'capacity', label: 'Capacity' },
      { key: 'active', label: 'Active' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'cabin_no', label: 'Cabin No', type: 'text' },
      { name: 'short_name', label: 'Short Name', type: 'text' },
      { name: 'cabin_type', label: 'Type', type: 'text' },
      { name: 'location', label: 'Location', type: 'text' },
      { name: 'tier', label: 'Tier', type: 'text' },
      { name: 'capacity', label: 'Capacity', type: 'number' },
      { name: 'max_guest', label: 'Max Guest', type: 'number' },
      { name: 'max_therapist', label: 'Max Therapist', type: 'number' },
      { name: 'active', label: 'Active', type: 'checkbox' },
      { name: 'department', label: 'Department', type: 'text' },
      { name: 'narration', label: 'Narration', type: 'text' },
      { name: 'seq_no', label: 'Seq No', type: 'number' },
      { name: 'cabin_seq_no', label: 'Cabin Seq No', type: 'text' },
      { name: 'main_cabin_no', label: 'Main Cabin No', type: 'text' },
    ],
  },
  equipment: {
    label: 'Equipment',
    path: 'equipment',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'total_quantity', label: 'Quantity' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'total_quantity', label: 'Total Quantity', type: 'number', required: true },
    ],
  },
  treatments: {
    label: 'Services',
    path: 'treatments',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'short_name', label: 'Code' },
      { key: 'type', label: 'Type' },
      { key: 'duration', label: 'Duration' },
      { key: 'preparation_time', label: 'Prep' },
      { key: 'therapists_required', label: 'Therapists' },
      { key: 'max_guests', label: 'Max Guests' },
      { key: 'base_price', label: 'Price' },
      { key: 'tax_rate', label: 'Tax Rate' },
      { key: 'gender_specific', label: 'Gender' },
      { key: 'tier_level', label: 'Tier' },
      { key: 'products_used', label: 'Products' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'short_name', label: 'Short Name', type: 'text' },
      { name: 'external_id', label: 'External ID', type: 'text' },
      { name: 'type', label: 'Type', type: 'select', options: [] },
      { name: 'duration', label: 'Duration (min)', type: 'number', required: true },
      { name: 'preparation_time', label: 'Preparation (min)', type: 'number', required: true },
      { name: 'first_start_time', label: 'First Start (min since 00:00)', type: 'number' },
      { name: 'last_end_time', label: 'Last End (min since 00:00)', type: 'number' },
      { name: 'seq_no', label: 'Sequence No', type: 'text' },
      { name: 'therapists_required', label: 'Therapists Required', type: 'number' },
      { name: 'max_guests', label: 'Max Guests', type: 'number' },
      { name: 'base_price', label: 'Base Price', type: 'number' },
      { name: 'tax_rate', label: 'Tax Rate', type: 'number' },
      { name: 'tier_level', label: 'Tier Level', type: 'text' },
      { name: 'tier_value', label: 'Tier Value', type: 'number' },
      { name: 'gender_specific', label: 'Gender Specific', type: 'select', options: [
        { value: 'Yes', label: 'Yes - Same gender required' },
        { value: 'No', label: 'No - Any gender allowed' },
      ] },
      { name: 'allowed_start_times', label: 'Allowed Start Times', type: 'text', placeholder: 'e.g. 14:45, 15:45 or 2:45 PM shorthand' },
      { name: 'quantity', label: 'Quantity', type: 'text' },
      { name: 'products_used', label: 'Products Used', type: 'text' },
      { name: 'equipment_used', label: 'Equipment Used', type: 'text' },
      { name: 'sub_category', label: 'Sub-Category', type: 'text' },
      { name: 'narration', label: 'Narration', type: 'textarea' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'short_description', label: 'Short Description', type: 'textarea' },
      { name: 'benefits', label: 'Benefits', type: 'textarea' },
      { name: 'booking_instructions', label: 'Booking Instructions', type: 'textarea' },
      { name: 'informed_consent', label: 'Informed Consent', type: 'textarea' },
      { name: 'treatment_checklist', label: 'Treatment Checklist', type: 'textarea' },
      { name: 'image_url', label: 'Image URL', type: 'text' },
      { name: 'thumbnail_url', label: 'Thumbnail URL', type: 'text' },
      { name: 'background_url', label: 'Background URL', type: 'text' },
    ],
  },
  therapists: {
    label: 'Staff',
    path: 'therapists',
    columns: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'role', label: 'Role' },
      { key: 'title', label: 'Title' },
      { key: 'designation', label: 'Designation' },
      { key: 'is_active', label: 'Status' },
      { key: 'exit_date', label: 'Exit Date' },
      { key: 'external_aiims_id', label: 'AIIMS ID' },
      { key: 'level', label: 'Level' },
      { key: 'gender', label: 'Gender' },
      { key: 'department', label: 'Department' },
      { key: 'phone', label: 'Phone' },
    ],
    fields: [
      { name: 'name', label: 'Full Name', type: 'text', required: true },
      { name: 'first_name', label: 'First Name', type: 'text' },
      { name: 'last_name', label: 'Last Name', type: 'text' },
      { name: 'title', label: 'Title', type: 'text' },
      { name: 'level', label: 'Level', type: 'text' },
      { name: 'role', label: 'Role', type: 'select', options: [] },
      { name: 'gender', label: 'Gender', type: 'select', options: [
        { value: 'M', label: 'Male' },
        { value: 'F', label: 'Female' },
        { value: 'Other', label: 'Other' },
      ] },
      { name: 'designation', label: 'Designation', type: 'select', options: [] },
      { name: 'department', label: 'Department', type: 'select', options: [] },
      { name: 'external_aiims_id', label: 'AIIMS ID', type: 'text', help: 'External AIIMS doctor ID for sync' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'phone', label: 'Phone', type: 'text' },
      { name: 'is_active', label: 'Active (can be rostered)', type: 'checkbox' },
      { name: 'exit_date', label: 'Last Working Day (exit date)', type: 'date', help: 'Staff inactive for scheduling after this date' },
    ],
  },
  tier_pricing: {
    label: 'Tier Pricing',
    path: 'tier-pricing',
    icon: 'AttachMoneyIcon',
    columns: [
      { key: 'tier_level', label: 'Tier Level' },
      { key: 'tier_value', label: 'Base Price (₹)' },
      { key: 'service_count', label: 'Services' },
      { key: 'description', label: 'Description' },
    ],
    fields: [
      { name: 'tier_level', label: 'Tier Level (e.g., T1, T2, T3...)', type: 'text', required: true },
      { name: 'tier_value', label: 'Base Price (₹)', type: 'number', required: true },
      { name: 'description', label: 'Description', type: 'text' },
    ],
  },
};

/** List columns that are read-only in the table (synced / computed / modal-only). */
export const ENTITY_READONLY_LIST_FIELDS = {
  tier_pricing: ['service_count', 'tier_level', 'description'],
  treatments: ['external_id'],
  therapists: ['is_active', 'exit_date', 'external_aiims_id'],
  rooms: ['name'],
  equipment: [],
};

export const isListReadOnly = (entityKey, fieldName) => {
  const set = ENTITY_READONLY_LIST_FIELDS[entityKey];
  return set ? set.includes(fieldName) : false;
};

/** Fields editable inline in the table (per-cell save). */
export const ENTITY_INLINE_FIELDS = {
  tier_pricing: new Set(['tier_value']),
  equipment: new Set(['name', 'total_quantity']),
  rooms: new Set([
    'cabin_no', 'cabin_type', 'location', 'capacity', 'active',
    'short_name', 'tier', 'max_guest', 'max_therapist', 'department',
    'seq_no', 'cabin_seq_no', 'main_cabin_no', 'narration',
  ]),
  therapists: new Set([
    'first_name', 'last_name', 'title', 'level', 'gender',
    'designation', 'phone', 'role', 'department', 'email',
  ]),
  treatments: new Set([
    'short_name', 'type', 'duration', 'preparation_time',
    'therapists_required', 'max_guests', 'base_price', 'tax_rate',
    'tier_level', 'tier_value', 'gender_specific', 'allowed_start_times',
    'first_start_time', 'last_end_time', 'seq_no', 'quantity',
    'products_used', 'equipment_used', 'sub_category',
  ]),
};

/** Additional list columns beyond default `columns` (optional in column picker). */
export const ENTITY_LIST_EXTRA = {
  rooms: ['short_name', 'tier', 'max_guest', 'max_therapist', 'department', 'seq_no', 'cabin_seq_no', 'main_cabin_no', 'narration'],
  treatments: ['tier_value', 'external_id', 'first_start_time', 'last_end_time', 'allowed_start_times', 'equipment_used', 'sub_category'],
  therapists: ['name', 'email'],
  tier_pricing: ['service_count'],
  equipment: [],
};

export const getDefaultColumnKeys = (entityKey) => {
  const entity = ENTITIES[entityKey];
  if (!entity) return [];
  return (entity.columns || []).map((c) => c.key);
};

export const getAllListFieldKeys = (entityKey) => {
  const defaults = getDefaultColumnKeys(entityKey);
  const extra = ENTITY_LIST_EXTRA[entityKey] || [];
  const seen = new Set();
  const keys = [];
  [...defaults, ...extra].forEach((k) => {
    if (!seen.has(k)) {
      seen.add(k);
      keys.push(k);
    }
  });
  return keys;
};

export const getFieldDef = (entityKey, fieldName) => {
  const entity = ENTITIES[entityKey];
  if (!entity) return null;
  return entity.fields.find((f) => f.name === fieldName) || null;
};

export const getListFieldDefs = (entityKey) => {
  const keys = getAllListFieldKeys(entityKey);
  return keys
    .map((key) => {
      const field = getFieldDef(entityKey, key);
      if (!field) {
        return {
          name: key,
          label: key,
          type: 'text',
          readOnly: key === 'service_count',
        };
      }
      return field;
    });
};

export const isInlineEditable = (entityKey, fieldName) => {
  const field = getFieldDef(entityKey, fieldName);
  if (!field || field.readOnly) return false;
  if (field.type === 'textarea') return false;
  const inlineSet = ENTITY_INLINE_FIELDS[entityKey];
  return inlineSet ? inlineSet.has(fieldName) : false;
};

export const getColumnLabel = (entityKey, fieldName) => {
  const fromCol = ENTITIES[entityKey]?.columns?.find((c) => c.key === fieldName);
  if (fromCol) return fromCol.label;
  const field = getFieldDef(entityKey, fieldName);
  return field?.label || fieldName;
};

const STORAGE_PREFIX = 'adminColumns_';

export const loadVisibleColumnKeys = (entityKey) => {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${entityKey}`);
    if (!raw) return getDefaultColumnKeys(entityKey);
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return getDefaultColumnKeys(entityKey);
    const allowed = new Set(getAllListFieldKeys(entityKey));
    const filtered = parsed.filter((k) => allowed.has(k));
    return filtered.length > 0 ? filtered : getDefaultColumnKeys(entityKey);
  } catch {
    return getDefaultColumnKeys(entityKey);
  }
};

export const saveVisibleColumnKeys = (entityKey, keys) => {
  localStorage.setItem(`${STORAGE_PREFIX}${entityKey}`, JSON.stringify(keys));
};

export const normalizeMoneyKey = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : String(num);
};

export const isMoneyField = (fieldName, field) => {
  if (field?.type !== 'number') return false;
  return /price|value|rate/i.test(fieldName);
};

/** Parse currency input and keep 2 decimal precision. */
export const roundMoney = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const num = Number(String(value).replace(/,/g, '').trim());
  if (Number.isNaN(num)) return null;
  return Math.round(num * 100) / 100;
};

/** Format for display / inline editor (always 2 decimal places). */
export const formatMoneyInputValue = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const num = roundMoney(value);
  if (num === null) return String(value);
  return num.toFixed(2);
};

export const formatCellDisplay = (entityKey, fieldName, value, row) => {
  const field = getFieldDef(entityKey, fieldName);
  if (fieldName === 'is_active') {
    return row.is_active === false ? 'Inactive' : 'Active';
  }
  if (fieldName === 'active') {
    return value === false || value === 'false' ? 'No' : 'Yes';
  }
  if (field?.type === 'checkbox') {
    return value ? 'Yes' : 'No';
  }
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (isMoneyField(fieldName, field)) {
    const num = Number(value);
    if (!Number.isNaN(num)) {
      return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
  if (field?.type === 'textarea' && String(value).length > 48) {
    return `${String(value).slice(0, 48)}…`;
  }
  return String(value);
};

/** Minutes since midnight (integers) — not comma-separated clock strings like allowed_start_times. */
export const isMinutesSinceMidnightField = (fieldName) =>
  fieldName === 'first_start_time' || fieldName === 'last_end_time';

export const areFieldValuesEqual = (field, a, b) => {
  if (!field) return String(a ?? '') === String(b ?? '');
  if (field.type === 'checkbox') return Boolean(a) === Boolean(b);
  if (field.type === 'number' || isMoneyField(field.name, field)) {
    if ((a === '' || a == null) && (b === '' || b == null)) return true;
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  }
  return String(a ?? '') === String(b ?? '');
};

export const cleanFieldValueForApi = (field, value) => {
  if (!field) return value;
  if (field.type === 'checkbox') {
    return Boolean(value);
  }
  if (isMoneyField(field.name, field)) {
    return roundMoney(value);
  }
  if (
    field.type === 'number' ||
    isMinutesSinceMidnightField(field.name) ||
    (field.name.includes('price') || field.name.includes('rate') || field.name.includes('value'))
  ) {
    if (value === '' || value === null || value === undefined) return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  }
  if (field.name === 'allowed_start_times') {
    if (value === null || value === undefined) return null;
    const trimmed = String(value).trim();
    return trimmed === '' ? null : trimmed;
  }
  return value;
};

export const buildInlineSavePayload = (entityKey, row, fieldName, newValue) => {
  const field = getFieldDef(entityKey, fieldName);
  const cleaned = cleanFieldValueForApi(field, newValue);
  const payload = { ...row, [fieldName]: cleaned };
  // Drop computed / non-persisted keys
  delete payload.service_count;
  return payload;
};

export const getTierPricingRowId = (row) =>
  `${row.tier_level}::${normalizeMoneyKey(row.tier_value)}`;

export const getAdminRowId = (row, entityKey) => {
  if (entityKey === 'tier_pricing') return getTierPricingRowId(row);
  return row.id;
};

/** Normalize API rows so table code can use `id` (tier pricing has no numeric id). */
export const normalizeAdminRows = (entityKey, rows) => {
  if (!Array.isArray(rows)) return [];
  if (entityKey === 'tier_pricing') {
    return rows.map((row) => ({ ...row, id: getTierPricingRowId(row) }));
  }
  return rows;
};

export const buildTierPricingSavePayload = (row) => ({
  tier_value: row.tier_value,
  description: row.description ?? null,
});

export const buildRowSavePayload = (entityKey, row) => {
  if (entityKey === 'tier_pricing') {
    return buildTierPricingSavePayload(row);
  }
  const payload = { ...row };
  delete payload.service_count;
  return payload;
};

export const validateInlineField = (field, value) => {
  if (!field) return { ok: true };
  if (field.required) {
    if (field.type === 'checkbox') {
      // checkbox is always valid
    } else if (value === null || value === undefined || value === '') {
      return { ok: false, message: `${field.label} is required` };
    }
  }
  if (
    field.type === 'number' ||
    isMinutesSinceMidnightField(field.name) ||
    (field.name.includes('price') || field.name.includes('rate') || field.name.includes('value'))
  ) {
    if (value === '' || value === null || value === undefined) {
      if (field.required) return { ok: false, message: `${field.label} must be a number` };
      return { ok: true };
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
      return { ok: false, message: `${field.label} must be a valid number` };
    }
  }
  return { ok: true };
};

const escapeCsvCell = (value) => {
  const str = String(value ?? '');
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const exportAdminTableCsv = (entityLabel, rows, columnKeys, entityKey) => {
  const headers = columnKeys.map((k) => getColumnLabel(entityKey, k));
  const lines = [headers.map(escapeCsvCell).join(',')];
  rows.forEach((row) => {
    const cells = columnKeys.map((k) =>
      escapeCsvCell(formatCellDisplay(entityKey, k, row[k], row))
    );
    lines.push(cells.join(','));
  });
  const csv = lines.join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const date = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.download = `${entityLabel.replace(/\s+/g, '_').toLowerCase()}_${date}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
